import { useState } from 'react';
import StickyAnchorNav from '../../components/ui/StickyAnchorNav';
import MobileNavDrawer from '../../components/ui/MobileNavDrawer';
import ScrollProgressIndicator from '../../components/ui/ScrollProgressIndicator';
import HeroSection from './components/HeroSection';
import TrustBar from './components/TrustBar';
import SecuritySection from './components/SecuritySection';
import FriendFindingSection from './components/FriendFindingSection';
import WebinarsSection from './components/WebinarsSection';

import SolutionSection from './components/SolutionSection';
import ProcessSection from './components/ProcessSection';
import TimelineSection from './components/TimelineSection';
import FAQSection from './components/FAQSection';
import TrustSignalsSection from './components/TrustSignalsSection';
import PricingSection from './components/PricingSection';

import Footer from './components/Footer';
import ContactButtons from './components/ContactButtons';
import { Helmet } from 'react-helmet';
import type {
  HeroContent,
  TrustMetric,
  ProblemStat,
  ProcessStep,
  TimelineMilestone,
  PricingPackage,
  FAQItem } from
'./types';
import TechnicalDocumentationSection from './components/TechnicalDocumentationSection';



const AIDatingServiceLandingPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const heroContent: HeroContent = {
    headline: "Сервис умных ИИ знакомств",
    subheadline: "Пройдите бесплатное тестирование 7 каналов мышления и создайте цифровой двойник для автоматического поиска наилучшей пары.",
    primaryCTA: "Пройти бесплатный тест",
    secondaryCTA: "Узнать больше"
  };

  const trustMetrics: TrustMetric[] = [
  { id: "active-matches", value: 247, label: "Цифровых двойников в активном поиске", suffix: "" },
  { id: "avg-time", value: 1000, label: "Виртуальных свиданий проведено за вас", suffix: "+" },
  { id: "success-rate", value: 94, label: "Точность подбора по 7 каналам", suffix: "%" }];


  const problemStats: ProblemStat[] = [
  { id: "swipe-hours", label: "Часов свайпинга в неделю", value: 12, unit: "часов" },
  { id: "conversations", label: "Поверхностных разговоров", value: 45, unit: "чатов" },
  { id: "real-connections", label: "Настоящих связей", value: 2, unit: "человека" }];


  const profileDimensions: ProfileDimension[] = [
  {
    id: "values",
    title: "Ценности и жизненные приоритеты",
    description: "Глубокий анализ ваших жизненных приоритетов, моральных принципов и того, что действительно важно в отношениях. AI изучает ваши взгляды на семью, карьеру, религию, финансы и жизненную философию.",
    icon: "Heart",
    sampleQuestions: [
    "Что для вас важнее: карьера или семья?",
    "Как вы относитесь к финансовой независимости?",
    "Какую роль играет религия в вашей жизни?",
    "Что значит для вас успех в жизни?",
    "Какие моральные принципы для вас непоколебимы?"]

  },
  {
    id: "lifestyle",
    title: "Образ жизни и повседневные привычки",
    description: "Ваши повседневные привычки, предпочтения в проведении времени и жизненный ритм. AI анализирует режим дня, активность, социальные предпочтения и бытовые привычки.",
    icon: "Coffee",
    sampleQuestions: [
    "Как вы предпочитаете проводить выходные?",
    "Насколько важна для вас физическая активность?",
    "Вы жаворонок или сова?",
    "Как часто вы путешествуете?",
    "Какое значение имеет порядок в доме?"]

  },
  {
    id: "communication",
    title: "Стиль общения и разрешения конфликтов",
    description: "Как вы выражаете эмоции, решаете конфликты и поддерживаете близость в отношениях. AI оценивает вашу открытость, способы выражения чувств и паттерны общения.",
    icon: "MessageCircle",
    sampleQuestions: [
    "Как вы предпочитаете решать разногласия?",
    "Насколько открыто вы говорите о чувствах?",
    "Как часто вам нужно общение с партнёром?",
    "Как вы реагируете на критику?",
    "Какой стиль общения для вас комфортен?"]

  },
  {
    id: "goals",
    title: "Цели отношений и видение будущего",
    description: "Ваши ожидания от отношений, планы на будущее и представление об идеальном партнёрстве. AI определяет серьёзность намерений, планы на семью и совместные цели.",
    icon: "Target",
    sampleQuestions: [
    "Вы ищете серьёзные отношения или что-то более лёгкое?",
    "Планируете ли вы детей в будущем?",
    "Как вы видите совместную жизнь через 5 лет?",
    "Что для вас значит идеальный брак?",
    "Готовы ли вы к компромиссам ради отношений?"]

  },
  {
    id: "personality",
    title: "Личностные черты и темперамент",
    description: "Ваш темперамент, характер и уникальные особенности, которые делают вас собой. AI анализирует интроверсию/экстраверсию, эмоциональность, спонтанность и способы справляться со стрессом.",
    icon: "User",
    sampleQuestions: [
    "Вы интроверт или экстраверт?",
    "Как вы справляетесь со стрессом?",
    "Что вас вдохновляет и мотивирует?",
    "Насколько вы эмоциональный человек?",
    "Предпочитаете планировать или действовать спонтанно?"]

  },
  {
    id: "interests",
    title: "Интересы и увлечения",
    description: "Ваши хобби, увлечения и то, чем вы любите заниматься в свободное время. AI сопоставляет совместимость интересов и находит точки пересечения для совместных занятий.",
    icon: "Sparkles",
    sampleQuestions: [
    "Какие у вас хобби и увлечения?",
    "Как вы относитесь к путешествиям?",
    "Какую музыку и фильмы вы предпочитаете?",
    "Занимаетесь ли вы спортом или творчеством?",
    "Какие книги вас вдохновляют?"]

  },
  {
    id: "dealbreakers",
    title: "Критичные факторы и предпочтения",
    description: "Важные аспекты, которые могут стать решающими в отношениях — как положительные, так и отрицательные. AI учитывает ваши абсолютные требования и непреодолимые препятствия.",
    icon: "AlertCircle",
    sampleQuestions: [
    "Что для вас абсолютно неприемлемо в партнёре?",
    "Какие качества обязательно должны быть?",
    "Есть ли у вас строгие требования к внешности?",
    "Какие привычки вы не сможете терпеть?",
    "Что для вас является сделкой, которая разрушает отношения?"]

  }];


  const processSteps: ProcessStep[] = [
  {
    id: "test",
    title: "Тестирование 7 каналов мышления",
    description: "Пройдите бесплатное тестирование 7 каналов коммуникации. Узнайте принципы вашего общения и восприятия мира, получите инструкции по подбору партнёра.",
    icon: "Brain",
    duration: "15 минут"
  },
  {
    id: "digitization",
    title: "Создание цифрового двойника",
    description: "Часовое видеоинтервью и создание вашего цифрового двойника. Двойник размещается в облаке для симуляции свиданий.",
    icon: "Video",
    duration: "1 час"
  },
  {
    id: "simulation",
    title: "Симуляция тысяч свиданий",
    description: "Ваш цифровой двойник автоматически проводит тысячи виртуальных свиданий с другими двойниками, проверяя совместимость по всем параметрам.",
    icon: "Users",
    duration: "Непрерывно"
  },
  {
    id: "match",
    title: "Идеальное совпадение 99,8%+",
    description: "Вы получаете лучшее совпадение из тысяч симуляций — только те пары, где совместимость 99,8% и выше.",
    icon: "Heart",
    duration: "2-4 недели"
  }];


  const timelineMilestones: TimelineMilestone[] = [
  {
    id: "week1",
    week: 1,
    title: "Интервью и создание профиля",
    description: "Вы проходите детальное интервью с нашим AI-специалистом. Создаётся ваш цифровой профиль и запускается процесс поиска.",
    icon: "Calendar"
  },
  {
    id: "week2-3",
    week: 2,
    title: "Активный поиск совпадений",
    description: "Ваш AI-аватар проводит сотни виртуальных знакомств. Алгоритм анализирует совместимость и отбирает лучших кандидатов.",
    icon: "Search"
  },
  {
    id: "week4-6",
    week: 4,
    title: "Первые качественные совпадения",
    description: "Вы получаете первые профили с высокой совместимостью. Начинаете реальное общение с предварительно отобранными людьми.",
    icon: "Users"
  },
  {
    id: "week8-12",
    week: 8,
    title: "Реальные встречи",
    description: "Организуются первые свидания с совместимыми партнёрами. Продолжается поиск и уточнение предпочтений на основе обратной связи.",
    icon: "Coffee"
  },
  {
    id: "week12",
    week: 12,
    title: "Успешное совпадение",
    description: "Средний пользователь находит совместимого партнёра для серьёзных отношений. Поддержка продолжается до достижения результата.",
    icon: "Heart"
  }];


  const pricingPackages: PricingPackage[] = [];



  const faqs: FAQItem[] = [
  {
    id: "faq1",
    question: "Как цифровой двойник может найти мне идеального партнёра?",
    answer: "Пройдите бесплатное тестирование 7 каналов мышления и получите инструкции по подбору партнёра. Затем создайте цифровой двойник бесплатно: часовое видеоинтервью, создание цифрового двойника и размещение в облаке для симуляции тысяч свиданий. Вы получаете только лучшие совпадения с точностью 99,8%+."
  },
  {
    id: "faq2",
    question: "Насколько безопасны мои данные?",
    answer: "Безопасность данных — наш главный приоритет. Мы используем шифрование военного уровня (AES-256), соблюдаем стандарты GDPR и ISO 27001. Ваши личные данные никогда не передаются третьим лицам. Цифровой двойник работает в защищённой среде, и вы полностью контролируете, какую информацию раскрывать."
  },
  {
    id: "faq3",
    question: "Сколько времени занимает процесс?",
    answer: "Тестирование 7 каналов мышления — 15 минут. Создание цифрового двойника — 1 час видеоинтервью. После оцифровки ваш цифровой двойник начинает автоматический поиск. Первые результаты с совместимостью 99,8%+ вы получаете через 2-4 недели."
  },
  {
    id: "faq4",
    question: "Чем это отличается от обычных приложений знакомств?",
    answer: "Это первый полностью автоматизированный сервис на основе социальной инженерии, ИИ и цифровых двойников. В традиционных приложениях вы сами свайпите, переписываетесь, проверяете на мошенников. Здесь цифровой двойник делает всю работу за вас — вы получаете только готовые результаты тысяч свиданий с совместимостью 99,8%+."
  },
  {
    id: "faq5",
    question: "Что если мне не понравятся предложенные совпадения?",
    answer: "Цифровой двойник постоянно учится на вашей обратной связи. После каждого совпадения вы можете указать, что вам понравилось или нет, и алгоритм корректирует поиск. Вы можете в любой момент приостановить поиск и вернуться позже."
  },
  {
    id: "faq6",
    question: "Могу ли я контролировать процесс поиска?",
    answer: "Абсолютно! Вы можете в любой момент обновить свои предпочтения, добавить новые критерии или исключить определённые характеристики. Цифровой двойник работает по вашим правилам. Вы можете приостановить поиск в любой момент и возобновить, когда готовы."
  }];



  const handlePrimaryCTA = () => {
    window.open('https://t.me/Aidating_love7_bot?start=001_res', '_blank', 'noopener,noreferrer');
  };

  const handleSecondaryCTA = () => {
    const howItWorksSection = document.querySelector('#how-it-works');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSelectPackage = (packageId: string) => {
    console.log('Selected package:', packageId);
    // Package selection logic can be added here
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AI Dating Service - Найди свою идеальную пару с помощью ИИ</title>
        <meta name="description" content="Первый в мире полностью автоматизированный сервис знакомств на основе AI. Наша технология анализирует 127 параметров личности для создания идеальных пар." />
      </Helmet>

      <ScrollProgressIndicator />
      <StickyAnchorNav
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen} />

      <MobileNavDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)} />


      <main>
        <HeroSection
          content={heroContent}
          onPrimaryCTA={handlePrimaryCTA}
          onSecondaryCTA={handleSecondaryCTA} />

        <TrustBar metrics={trustMetrics} />
        <SolutionSection />
        <SecuritySection />
        <ProcessSection steps={processSteps} />
        <TimelineSection milestones={timelineMilestones} />
        <TrustSignalsSection />
        <TechnicalDocumentationSection />
        <PricingSection />
        <FriendFindingSection />
        <WebinarsSection />
        <FAQSection faqs={faqs} />
      </main>

      <Footer />
      <ContactButtons />
    </div>);

};

export default AIDatingServiceLandingPage;